// components/ai/HuggingFaceChatBox.tsx
"use client";

import React, { useState, useRef, useEffect } from "react";
import {
  PaperAirplaneIcon,
  UserCircleIcon,
  DocumentArrowDownIcon,
  TrashIcon,
  ArrowPathIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import jsPDF from "jspdf";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { useAuthSession } from "@/hooks/useAuthSession";

interface Message {
  id: string;
  content: string;
  role: "user" | "assistant";
  timestamp: Date;
  isLoading?: boolean;
}

interface HuggingFaceChatBoxProps {
  className?: string;
  initialQuery?: string | null;
  externalTrigger?: string | null;
}

type Persona = "general" | "yoga_master" | "strength_coach" | "nutritionist";

const PERSONAS: Record<
  Persona,
  { name: string; icon: string; prompt: string; color: string }
> = {
  general: {
    name: "Prana",
    icon: "🌿",
    prompt: "You are Prana, a warm and knowledgeable AI wellness coach on the Relaxing Routine platform. You help users with yoga, workouts, nutrition, and holistic wellbeing. Be friendly, encouraging, and use the user's name when available.",
    color: "from-orange-500 to-amber-500",
  },
  yoga_master: {
    name: "Yoga Master",
    icon: "🧘‍♀️",
    prompt:
      "You are an expert Yoga Master. Focus on mindfulness, breath, alignment, and spiritual well-being.",
    color: "from-purple-500 to-pink-500",
  },
  strength_coach: {
    name: "Strength Coach",
    icon: "💪",
    prompt:
      "You are a high-performance Strength Coach. Focus on hypertrophy, sets, reps, form, and discipline.",
    color: "from-orange-500 to-red-500",
  },
  nutritionist: {
    name: "Nutritionist",
    icon: "🥗",
    prompt:
      "You are a certified Nutritionist. Focus on macronutrients, hydration, healthy eating habits, and fueling the body.",
    color: "from-green-500 to-emerald-500",
  },
};

const HuggingFaceChatBox: React.FC<HuggingFaceChatBoxProps> = ({
  className = "",
  initialQuery = null,
  externalTrigger = null,
}) => {
  const { user } = useAuthSession();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      content:
        "Hey there! 🌿 I'm **Prana**, your AI wellness guide. I can help you with yoga sequences, custom workout plans, nutrition advice, and anything else on your wellness journey. How can I help you today?",
      role: "assistant",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<
    "connected" | "connecting" | "error"
  >("connected");
  const [hasAutoSent, setHasAutoSent] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load History
  useEffect(() => {
    const fetchHistory = async () => {
      if (!user) return;
      setIsHistoryLoading(true);
      try {
        const res = await fetch("/api/ai/history");
        const data = await res.json();
        if (data.success && data.messages.length > 0) {
          // Convert string timestamps back to Date objects
          const formattedMessages = data.messages.map((m: any) => ({
            ...m,
            timestamp: new Date(m.timestamp)
          }));
          setMessages(formattedMessages);
        }
      } catch (err) {
        console.error("Failed to load history");
      } finally {
        setIsHistoryLoading(false);
      }
    };

    fetchHistory();
  }, [user]);

  // Save History
  useEffect(() => {
    const saveHistory = async () => {
      if (!user || messages.length <= 1 || isLoading) return;

      try {
        await fetch("/api/ai/history", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ messages: messages.filter(m => !m.isLoading) })
        });
      } catch (err) {
        console.error("Failed to save history");
      }
    };

    // Debounce save to avoid excessive API calls
    const timer = setTimeout(saveHistory, 2000);
    return () => clearTimeout(timer);
  }, [messages, user, isLoading]);

  const clearHistory = async () => {
    if (!confirm("Are you sure you want to clear your conversation history?")) return;

    try {
      if (user) {
        await fetch("/api/ai/history", { method: "DELETE" });
      }
      setMessages([
        {
          id: "1",
          content: "History cleared. How can I help you today?",
          role: "assistant",
          timestamp: new Date(),
        },
      ]);
      toast.success("Conversation history cleared");
    } catch (err) {
      toast.error("Failed to clear history");
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle initial query from search
  useEffect(() => {
    if (initialQuery && initialQuery.trim() && !hasAutoSent) {
      setInput(initialQuery.trim());
      // Auto-send the initial query after a short delay, but only once
      const timer = setTimeout(() => {
        if (initialQuery.trim() && !isLoading && !hasAutoSent) {
          setHasAutoSent(true);
          handleSendMessage(initialQuery.trim());
          // Clear the input after auto-sending
          setInput("");
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [initialQuery, hasAutoSent]);

  // Handle external trigger from Smart Check-In
  useEffect(() => {
    if (externalTrigger && externalTrigger.trim() && !isLoading) {
      setInput(externalTrigger);
      // Auto-send after a small delay
      const timer = setTimeout(() => {
        if (externalTrigger.trim()) {
          handleSendMessage(externalTrigger.trim());
          setInput("");
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [externalTrigger]);

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || input.trim();
    if (!textToSend || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: textToSend,
      role: "user",
      timestamp: new Date(),
    };

    const loadingMessage: Message = {
      id: (Date.now() + 1).toString(),
      content: "Thinking...",
      role: "assistant",
      timestamp: new Date(),
      isLoading: true,
    };

    setMessages((prev) => [...prev, userMessage, loadingMessage]);
    if (!messageText) setInput("");
    setIsLoading(true);
    setConnectionStatus("connecting");

    try {
      // Prepend persona instruction to the message for the API
      const personaInstruction = `[System: ${PERSONAS.general.prompt}] User: `;
      const finalMessage = personaInstruction + textToSend;

      const response = await fetch("/api/ai/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: finalMessage,
          conversationHistory: messages.slice(-10),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setConnectionStatus("connected");

      if (data.success && data.data.message) {
        const assistantMessage: Message = {
          id: (Date.now() + 2).toString(),
          content: data.data.message,
          role: "assistant",
          timestamp: new Date(),
        };

        setMessages((prev) =>
          prev.filter((msg) => !msg.isLoading).concat([assistantMessage])
        );
      } else {
        throw new Error(data.error || "Failed to get AI response");
      }
    } catch (error) {
      console.error("Chat error:", error);
      setConnectionStatus("error");

      const errorMessage: Message = {
        id: (Date.now() + 3).toString(),
        content:
          "I apologize, but I'm having trouble connecting right now. Please try again in a moment.",
        role: "assistant",
        timestamp: new Date(),
      };

      setMessages((prev) =>
        prev.filter((msg) => !msg.isLoading).concat([errorMessage])
      );

      toast.error("Connection issue - please try again");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const downloadAsPDF = (content: string, timestamp: Date) => {
    try {
      // Create new PDF document
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      const maxWidth = pageWidth - 2 * margin;
      let currentY = 30;

      // Add header
      pdf.setFontSize(18);
      pdf.setFont(undefined, "bold");
      pdf.text("Relaxing Routine", margin, currentY);
      currentY += 10;

      pdf.setFontSize(14);
      pdf.text("AI Wellness Recommendation", margin, currentY);
      currentY += 20;

      // Add timestamp
      pdf.setFontSize(10);
      pdf.setFont(undefined, "normal");
      pdf.text(
        `Generated: ${timestamp.toLocaleDateString()} at ${timestamp.toLocaleTimeString()}`,
        margin,
        currentY
      );
      currentY += 20;

      // Add content
      pdf.setFontSize(12);

      // Split content into lines that fit the page width
      const lines = pdf.splitTextToSize(content, maxWidth);

      lines.forEach((line: string) => {
        // Check if we need a new page
        if (currentY > pdf.internal.pageSize.getHeight() - 30) {
          pdf.addPage();
          currentY = 30;
        }

        pdf.text(line, margin, currentY);
        currentY += 6;
      });

      // Add footer
      currentY += 20;
      if (currentY > pdf.internal.pageSize.getHeight() - 40) {
        pdf.addPage();
        currentY = 30;
      }

      pdf.setFontSize(10);
      pdf.setFont(undefined, "italic");
      pdf.text("Generated by Prana AI — Relaxing Routine", margin, currentY);
      pdf.text("Your Personal Wellness Companion", margin, currentY + 8);

      // Save the PDF
      const fileName = `wellness-recommendation-${timestamp.toISOString().split("T")[0]}.pdf`;
      pdf.save(fileName);

      toast.success("PDF recommendation downloaded successfully!");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF recommendation");
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case "connected":
        return "AI Online";
      case "connecting":
        return "Thinking...";
      case "error":
        return "Connection Issue";
    }
  };

  return (
    <div
      className={`flex flex-col h-full bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden ${className}`}
    >
      {/* Status bar — minimal, no title (parent context provides branding) */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-white shrink-0">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${connectionStatus === "connected" ? "bg-emerald-400" : "bg-orange-400"}`} />
            <span className={`relative inline-flex rounded-full h-2 w-2 ${connectionStatus === "connected" ? "bg-emerald-500" : "bg-orange-500"}`} />
          </span>
          <span className="text-xs text-slate-400">{getStatusText()}</span>
          {isHistoryLoading && (
            <ArrowPathIcon className="h-3 w-3 text-slate-300 animate-spin ml-1" />
          )}
        </div>
        {messages.length > 1 && (
          <button
            onClick={clearHistory}
            className="flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all"
            title="Clear conversation history"
          >
            <TrashIcon className="h-3.5 w-3.5" />
            <span>Clear</span>
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6 space-y-4 sm:space-y-6 bg-slate-50/50">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex items-end space-x-2 ${message.role === "user" ? "justify-end" : "justify-start"}`}
          >
            {message.role === "assistant" && (
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-sm text-sm">
                <SparklesIcon className="h-4 w-4 text-white" />
              </div>
            )}

            <div
              className={`max-w-[85%] sm:max-w-md lg:max-w-lg px-4 sm:px-5 py-3 sm:py-4 transition-all duration-300 animate-in fade-in slide-in-from-bottom-2 ${message.role === "user"
                ? "bg-gradient-to-br from-orange-500 to-amber-500 text-white rounded-2xl rounded-tr-none shadow-md shadow-orange-200/50"
                : message.isLoading
                  ? "bg-white text-slate-400 rounded-2xl rounded-tl-none border border-slate-100 shadow-sm"
                  : "bg-white text-slate-700 rounded-2xl rounded-tl-none border border-slate-100 shadow-sm relative overflow-hidden group/msg"
                }`}
            >
              {message.isLoading ? (
                <div className="flex space-x-1.5 items-center h-6">
                  <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              ) : (
                <div className="relative z-10">
                  <div 
                    className={`text-sm leading-relaxed space-y-2 [&>p]:mb-2 [&>ul]:list-disc [&>ul]:pl-5 [&>ol]:list-decimal [&>ol]:pl-5 [&>li]:mb-1 [&>h1]:font-bold [&>h1]:text-lg [&>h2]:font-bold [&>h2]:text-base [&>h3]:font-semibold [&>h3]:text-base [&>h3]:mt-3 [&>strong]:font-semibold [&_a]:text-blue-500 [&_a]:underline ${message.role === "user" ? "[&>p]:text-white" : ""}`}
                    dangerouslySetInnerHTML={{ 
                      __html: typeof window !== "undefined" 
                        ? DOMPurify.sanitize(marked.parse(message.content, { breaks: true }) as string) 
                        : message.content 
                    }} 
                  />
                  <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-slate-100">
                    <p className={`text-xs ${message.role === "user" ? "text-white/60" : "text-orange-400"}`}>
                      {message.timestamp.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                    {message.role === "assistant" && !message.isLoading && (
                      <button
                        onClick={() => downloadAsPDF(message.content, message.timestamp)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-orange-500 hover:text-orange-600 hover:bg-orange-50 rounded-md transition-all"
                        title="Download recommendation as PDF"
                      >
                        <DocumentArrowDownIcon className="h-3.5 w-3.5" />
                        <span>PDF</span>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {
              message.role === "user" && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-100 border border-orange-200 flex items-center justify-center overflow-hidden">
                  <UserCircleIcon className="h-6 w-6 text-orange-500" />
                </div>
              )
            }
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 sm:p-4 bg-white border-t border-gray-100">
        <div className="flex items-end space-x-2 bg-gray-50 p-2 rounded-xl border border-gray-200 focus-within:border-orange-500 focus-within:ring-1 focus-within:ring-orange-500 transition-all duration-200">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask Prana anything..."
            className="flex-1 bg-transparent border-none focus:ring-0 resize-none text-sm sm:text-base max-h-32 py-2 px-2"
            rows={1}
            disabled={isLoading}
            style={{ minHeight: "44px" }}
          />
          <button
            onClick={() => handleSendMessage()}
            disabled={!input.trim() || isLoading}
            className="p-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm shadow-orange-200"
          >
            <PaperAirplaneIcon className="h-5 w-5" />
          </button>
        </div>

        {connectionStatus === "error" && (
          <div className="mt-2 text-center text-xs text-red-500">
            Connection issues detected. Please try again.
          </div>
        )}
      </div>
    </div >
  );
};

export default HuggingFaceChatBox;
